import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  FaMousePointer,
  FaArrowDown,
  FaChartBar,
  FaDesktop,
  FaClock,
  FaExclamationCircle,
} from 'react-icons/fa';
import { fetchInteractionMetrics } from '../services/interactionMetricsService';

// ─── Constants ─────────────────────────────────────────────────────────────────

/** Auto-refresh interval in milliseconds */
const REFRESH_INTERVAL_MS = 60_000;

/** Default metrics shown before data loads */
const DEFAULT_METRICS = {
  totalInteractions: 0,
  totalClicks: 0,
  totalScrolls: 0,
  avgScrollDepth: '0%',
  uniquePages: 0,
  uniqueDevices: 0,
};

// ─── Pure Helpers ──────────────────────────────────────────────────────────────

/**
 * Derives summary metrics from the raw interaction records array.
 *
 * @param {Array} records - Raw interaction data from the API
 * @returns {Object} Summary statistics
 */
const buildMetricsData = (records) => {
  if (!records || records.length === 0) return DEFAULT_METRICS;

  // Separate clicks and scrolls
  const clicks = records.filter((r) => r.interactionType === 'click');
  const scrolls = records.filter((r) => r.interactionType === 'scroll');
  
  // Calculate total clicks from clickCount field if available
  const totalClicks = clicks.reduce((sum, r) => sum + (r.clickCount || 1), 0);
  
  // Calculate total scrolls
  const totalScrolls = scrolls.reduce((sum, r) => sum + (r._count || 1), 0);
  
  // Total interactions
  const totalInteractions = totalClicks + totalScrolls;
  
  // Calculate average scroll depth from distribution
  const avgScrollDepth = scrolls.length > 0
    ? Math.round(scrolls.reduce((sum, r) => sum + (r.scrollDepth || 0), 0) / scrolls.length)
    : 0;

  // Get unique pages
  const uniquePages = new Set(records.map((r) => r.page)).size;
  
  // Get unique devices
  const uniqueDevices = new Set(records.map((r) => r.deviceId)).size;

  return {
    totalInteractions,
    totalClicks,
    totalScrolls,
    avgScrollDepth: `${avgScrollDepth}%`,
    uniquePages,
    uniqueDevices,
  };
};

// ─── Sub-components ────────────────────────────────────────────────────────────

/**
 * Displays a spinning loader centered on the page.
 */
const LoadingState = () => (
  <div className="flex-1 flex items-center justify-center bg-[#1E1F22] min-h-screen">
    <div className="flex items-center gap-3">
      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
      <span className="text-blue-300">Loading interaction metrics...</span>
    </div>
  </div>
);

/**
 * Summary card for a single metric.
 *
 * @param {string}   title - Card label
 * @param {string|number} value - Value to display
 * @param {React.FC} icon  - React icon component
 * @param {string}   color - Tailwind text-color class
 */
const StatCard = ({ title, value, icon: Icon, color = 'text-blue-400' }) => (
  <div className="bg-[#1D1E22] rounded-lg p-4 border border-gray-800">
    <div className="flex justify-between items-start">
      <div>
        <span className="text-gray-400 text-sm mb-2 block">{title}</span>
        <span className={`text-3xl font-bold ${color}`}>{value}</span>
      </div>
      <Icon className={`w-8 h-8 ${color}`} />
    </div>
  </div>
);

/**
 * Detailed records table showing individual interaction events.
 */
const InteractionRecordsTable = ({ records, loading, currentPage, rowsPerPage, onPageChange }) => {
  const startIdx = (currentPage - 1) * rowsPerPage;
  const paginatedRecords = records.slice(startIdx, startIdx + rowsPerPage);
  const totalPages = Math.ceil(records.length / rowsPerPage);

  if (loading) return null;

  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold text-white mb-4">Recent Interactions</h3>
      <div className="bg-[#1D1E22] rounded-lg border border-gray-800 overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#252629] border-b border-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Type</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Button ID</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Count/Depth</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Unique Pages</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Device</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRecords.length > 0 ? (
              paginatedRecords.map((record, idx) => (
                <tr key={idx} className="border-b border-gray-700 hover:bg-[#252629]">
                  <td className="px-4 py-3 text-sm text-gray-300 capitalize">
                    <span className={`px-2 py-1 rounded ${
                      record.interactionType === 'click' 
                        ? 'bg-blue-900 text-blue-200' 
                        : 'bg-green-900 text-green-200'
                    }`}>
                      {record.interactionType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">{record.buttonId || record.page || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {record.interactionType === 'scroll' 
                      ? `${record.scrollDepth || 0}%` 
                      : record.clickCount || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {record.uniquePages || record._count || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300 capitalize">{record.deviceType || 'all'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-4 py-6 text-center text-gray-400">
                  No interactions recorded yet
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-[#252629] border-t border-gray-700 flex justify-between items-center">
            <span className="text-sm text-gray-400">
              Page {currentPage} of {totalPages} ({records.length} total)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700"
              >
                Previous
              </button>
              <button
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────────

/**
 * InteractionMetrics Dashboard
 * Displays clicks, scroll depth, and interaction patterns.
 */
export default function InteractionMetrics() {
  const [metrics, setMetrics] = useState(DEFAULT_METRICS);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedInteractionType, setSelectedInteractionType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Fetch interaction metrics from the API
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        ...(selectedPage && { page: selectedPage }),
        ...(selectedInteractionType && { interactionType: selectedInteractionType }),
      };

      const response = await fetchInteractionMetrics(filters);

      if (response.success) {
        // Handle different response structures
        let metricsArray = [];
        
        // Check if response has metrics array (direct metrics)
        if (response.metrics && Array.isArray(response.metrics)) {
          metricsArray = response.metrics;
        }
        // Check if response has interaction object with topClicks and scrollDepth data
        else if (response.interaction) {
          // Process topClicks data
          if (response.interaction.topClicks && Array.isArray(response.interaction.topClicks)) {
            metricsArray = response.interaction.topClicks.map((item, idx) => ({
              id: idx,
              interactionType: 'click',
              buttonId: item.buttonId,
              clickCount: item.clickCount,
              uniquePages: item.uniquePages,
              page: 'aggregate',
              sessionId: `click_${idx}`,
              deviceId: 'aggregate',
              deviceType: 'all',
              timestamp: new Date().toISOString(),
            }));
          }
          
          // Add scrollDepth distribution data if available
          if (response.interaction.scrollDepth && response.interaction.scrollDepth.scrollDepthDistribution) {
            const distribution = response.interaction.scrollDepth.scrollDepthDistribution;
            const scrollMetrics = distribution.map((item, idx) => ({
              id: `scroll_${idx}`,
              interactionType: 'scroll',
              scrollDepth: parseInt(item.range.split('-')[1]),
              page: 'aggregate',
              sessionId: `scroll_${idx}`,
              deviceId: 'aggregate',
              deviceType: 'all',
              timestamp: new Date().toISOString(),
              _count: item.users
            }));
            metricsArray = [...metricsArray, ...scrollMetrics];
          }
        }
        
        setRecords(metricsArray);
        setMetrics(buildMetricsData(metricsArray));
        setLastRefresh(new Date());
      } else {
        setError('Failed to fetch interaction metrics');
        setRecords([]);
        setMetrics(DEFAULT_METRICS);
      }
    } catch (err) {
      console.error('Error fetching interaction metrics:', err);
      setError(err.message || 'Error fetching metrics');
      setRecords([]);
      setMetrics(DEFAULT_METRICS);
    } finally {
      setLoading(false);
    }
  }, [selectedPage, selectedInteractionType]);

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Get unique pages from records for filter dropdown
  const uniquePages = useMemo(() => {
    return [...new Set(records.map((r) => r.page))].sort();
  }, [records]);

  if (loading && records.length === 0) {
    return <LoadingState />;
  }

  return (
    <div className="flex-1 bg-[#1E1F22] p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Interaction Metrics</h1>
          <p className="text-gray-400">
            Track clicks, scroll depth, and user interactions across your experience
          </p>
          {lastRefresh && (
            <p className="text-xs text-gray-500 mt-2">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-900 border border-red-600 rounded-lg flex items-start gap-3">
            <FaExclamationCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-200 font-semibold">Error</p>
              <p className="text-red-300 text-sm">{error}</p>
              <button
                onClick={fetchData}
                className="mt-2 px-3 py-1 bg-red-700 hover:bg-red-600 text-white rounded text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Summary Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <StatCard
            title="Total Interactions"
            value={metrics.totalInteractions}
            icon={FaMousePointer}
            color="text-blue-400"
          />
          <StatCard
            title="Clicks"
            value={metrics.totalClicks}
            icon={FaMousePointer}
            color="text-green-400"
          />
          <StatCard
            title="Scrolls"
            value={metrics.totalScrolls}
            icon={FaArrowDown}
            color="text-purple-400"
          />
          <StatCard
            title="Avg Scroll Depth"
            value={metrics.avgScrollDepth}
            icon={FaChartBar}
            color="text-orange-400"
          />
          <StatCard
            title="Unique Pages"
            value={metrics.uniquePages}
            icon={FaMousePointer}
            color="text-cyan-400"
          />
          <StatCard
            title="Active Devices"
            value={metrics.uniqueDevices}
            icon={FaDesktop}
            color="text-pink-400"
          />
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Filter by Page</label>
            <select
              value={selectedPage}
              onChange={(e) => {
                setSelectedPage(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 bg-[#252629] border border-gray-700 rounded text-gray-300 focus:outline-none focus:border-blue-500"
            >
              <option value="">All Pages</option>
              {uniquePages.map((page) => (
                <option key={page} value={page}>
                  {page}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">Filter by Type</label>
            <select
              value={selectedInteractionType}
              onChange={(e) => {
                setSelectedInteractionType(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 bg-[#252629] border border-gray-700 rounded text-gray-300 focus:outline-none focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="click">Clicks</option>
              <option value="scroll">Scrolls</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">Rows per Page</label>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-2 bg-[#252629] border border-gray-700 rounded text-gray-300 focus:outline-none focus:border-blue-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchData}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded disabled:cursor-not-allowed"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Records Table */}
        <InteractionRecordsTable
          records={records}
          loading={loading}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
